<script>
import { onMounted, ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import { useModalStore } from "@/stores/modal.js";
import PasswordRequirements from "@/components/password_requirements.vue";

export default {
  components: {PasswordRequirements},
  setup() {
    // Stores
    const modalStore = useModalStore();

    // Variables
    const isLoading = ref(true);
    const firstName = ref("");
    const lastName = ref("");
    const username = ref("");
    const password = ref("");
    const passwordConfirmation = ref("");
    const termsConditions = ref(false);
    const showPassword = ref(false);
    const passwordValid = ref(false);

    const emailTaken = ref(false);

    // I18n
    const { t } = useI18n();

    onMounted(async () => {});

    const isFormValid = computed(() => {
      return (
        firstName.value &&
        lastName.value &&
        username.value &&
        passwordValid.value &&
        passwordConfirmation.value &&
        password.value === passwordConfirmation.value &&
        termsConditions.value
      );
    });

    const signup = async () => {
      // Defensive check: Prevent submission if form is not valid
      // This ensures users cannot bypass client-side validation by manipulating the DOM
      if (!isFormValid.value) {
        return;
      }

      try {
        const response = await fetch(
          `${import.meta.env.VITE_BACKEND_URL}/api/v2/servicechek/users/register`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              first_name: firstName.value,
              last_name: lastName.value,
              email: username.value,
              password: password.value,
              agree_to_tos: termsConditions.value,
            }),
          }
        );

        if (response.status >= 200 && response.status < 300) {
          modalStore.setModal(modalStore.modalTypeConstants.SIGN_UP_VERIFICATION);
        } else {
          emailTaken.value = true;
        }

      } catch (error) {
        console.error("Error creating user:", error);
      }
    };

    return {
      t,
      modalStore,
      isLoading,
      firstName,
      lastName,
      username,
      password,
      passwordConfirmation,
      termsConditions,
      showPassword,
      signup,
      isFormValid,
      passwordValid,
      emailTaken
    };
  },
};
</script>

<template>
  <div class="justify-center items-center w-full">
    <form class="px-8 md:px-16 py-4 md:py-8">
      <div class="mb-2">
        <span class="text-gray-600 font-semibold">{{ t("modal.sign_up.credit_card_notice") }}</span>
      </div>
      <div class="mb-4">
        <span class="font-bold text-xl">{{
          t("modal.sign_up.modal_body", { app_name: t("app_name") })
        }}</span>
      </div>
      <div class="flex justify-between items-center mb-4">
        <div class="pr-2 w-1/2">
          <label for="first_name" class="block text-gray-600 font-semibold">{{
            t("modal.sign_up.first_name_label")
          }}</label>
          <input
            type="text"
            id="first_name"
            name="first_name"
            v-model="firstName"
            class="w-full mt-1 p-2 px-4 border border-gray-300 rounded-md focus:outline-none focus:ring focus:ring-purple-300"
            :placeholder="t('modal.sign_up.first_name_placeholder')"
          >
        </div>
        <div class="pl-2 w-1/2">
          <label for="last_name" class="block text-gray-600 font-semibold">{{
            t("modal.sign_up.last_name_label")
          }}</label>
          <input
            type="text"
            id="last_name"
            name="last_name"
            v-model="lastName"
            class="w-full mt-1 p-2 px-4 border border-gray-300 rounded-md focus:outline-none focus:ring focus:ring-purple-300"
            :placeholder="t('modal.sign_up.last_name_placeholder')"
          >
        </div>
      </div>
      <div class="mb-4">
        <label for="email" class="block text-gray-600 font-semibold">{{
            t("modal.sign_up.email_label")
          }}</label>
        <input
          type="email"
          id="email"
          name="email"
          v-model="username"
          :class="['w-full mt-1 p-2 px-4 border rounded-md focus:outline-none focus:ring focus:ring-purple-300', emailTaken ? 'border-red-300' : 'border-gray-300']"
          :placeholder="t('modal.sign_up.email_placeholder')"
        >
        <div v-if="emailTaken" for="email" class="block text-red-600 font-semibold mt-2">
          {{ t("modal.sign_up.email_already_in_use") }}</div>
      </div>
      <div class="mb-4">
        <label for="password" class="block text-gray-600 font-semibold">{{
            t("modal.sign_up.password_label")
          }}</label>
        <div class="relative">
          <input
            v-model="password"
            :type="showPassword ? 'text' : 'password'"
            class="w-full mt-1 p-2 px-4 border border-gray-300 rounded-md focus:outline-none focus:ring focus:ring-purple-300"
            :placeholder="t('modal.sign_up.password_placeholder')"
            id="password"
            autocomplete="new-password"
          />
          <button
            type="button"
            @click="showPassword = !showPassword"
            class="absolute inset-y-0 top-1 right-0 px-3 flex rounded-r-md items-center focus:outline-none focus:ring focus:ring-purple-300 hover:opacity-75"
          >
            <img v-if="showPassword" src="../../assets/icons/visibility_off_24dp_FILL0_wght400_GRAD0_opsz24.svg" alt="Logo 1" class="text-white">
            <img v-else src="../../assets/icons/visibility_24dp_FILL0_wght400_GRAD0_opsz24.svg" alt="Logo 1" class="text-white">
          </button>
        </div>
      </div>
      <div class="mb-4">
        <label for="passwordConfirmation" class="block text-gray-600 font-semibold">{{
            t("modal.sign_up.password_confirmation_label")
          }}</label>
        <div class="relative">
          <input
            v-model="passwordConfirmation"
            :type="showPassword ? 'text' : 'password'"
            class="w-full mt-1 p-2 px-4 border border-gray-300 rounded-md focus:outline-none focus:ring focus:ring-purple-300"
            :placeholder="t('modal.sign_up.password_confirmation_placeholder')"
            id="passwordConfirmation"
            autocomplete="new-password"
          />
          <button
            type="button"
            @click="showPassword = !showPassword"
            class="absolute inset-y-0 top-1 right-0 px-3 flex rounded-r-md items-center focus:outline-none focus:ring focus:ring-purple-300 hover:opacity-75"
          >
            <img v-if="showPassword" src="../../assets/icons/visibility_off_24dp_FILL0_wght400_GRAD0_opsz24.svg" alt="Logo 1" class="text-white">
            <img v-else src="../../assets/icons/visibility_24dp_FILL0_wght400_GRAD0_opsz24.svg" alt="Logo 1" class="text-white">
          </button>
        </div>
      </div>
      <PasswordRequirements :password="password" :password_confirmation="passwordConfirmation" :password_confirmation_required="true" @allValid="(value) => passwordValid = value" />
      <div class="flex justify-start items-center mb-8">
        <input
          v-model="termsConditions"
          type="checkbox"
          class="blitzify-checkbox small-version border border-gray-300 appearance-none rounded-[0.25rem] border-[0.125rem] border-solid outline-none before:pointer-events-none before:absolute before:h-[0.500rem] before:w-[0.500rem] before:scale-0 before:rounded-full before:bg-transparent before:opacity-0 before:shadow-[0px_0px_0px_13px_transparent] before:content-[''] checked:border-primary checked:bg-primary checked:before:opacity-[0.16] checked:after:absolute checked:after:-mt-px checked:after:ml-[0.25rem] checked:after:block checked:after:h-[0.8125rem] checked:after:w-[0.375rem] checked:after:rotate-45 checked:after:border-[0.125rem] checked:after:border-l-0 checked:after:border-t-0 checked:after:border-solid checked:after:border-white checked:after:bg-transparent checked:after:content-[''] hover:cursor-pointer hover:before:opacity-[0.04] hover:before:shadow-[0px_0px_0px_13px_rgba(0,0,0,0.6)]"
        />
        <div class="flex flex-col pt-3">
          <span class="pl-2 text-gray-600 text-sm font-semibold">{{
            t("modal.sign_up.terms_conditions_agree")
          }}</span>
          <span class="pl-2 text-xs view-terms-conditions cursor-pointer hover:opacity-75" @click="() => modalStore.setModal(modalStore.modalTypeConstants.TERMS_AND_CONDITIONS)">{{ t('modal.sign_up.terms_conditions') }}</span>
        </div>
      </div>
      <div class="mb-4">
        <button type="button" :disabled="!isFormValid" class="w-full text-white py-2 px-4 font-semibold rounded-full create-button hover:opacity-75 disabled:opacity-50 disabled:cursor-not-allowed" @click="signup">{{ t('modal.sign_up.sign_up_button', { app_name: t('app_name') }) }}</button>
      </div>
      <div class="flex justify-center">
        <button class="block text-sm have-account hover:opacity-75" @click="modalStore.setModal(modalStore.modalTypeConstants.LOGIN)">{{ t('modal.sign_up.have_account') }}</button>
      </div>
    </form>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/styles/components/modals/sign_up';
</style>
